import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { ParticipantDomainService } from '../domain-services/participant-domain.service';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { AssessmentDomainService } from '../domain-services/assessment-domain.service';

@Component({
  selector: 'app-invite-participants',
  imports: [MatInputModule, MatButtonModule, RouterLink],
  templateUrl: './invite-participants.html',
  styleUrl: './invite-participants.scss',
})
export class InviteParticipants {
  private readonly participantDomain = inject(ParticipantDomainService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectDomain = inject(ProjectDomainService);
  private readonly assessmentDomain = inject(AssessmentDomainService);
  private readonly snackBar = inject(MatSnackBar);

  emailsText = signal('');
  isSubmitting = signal(false);
  apiError = signal<string | null>(null);
  isSendingSample = signal(false);

  errors = computed(() => {
    const raw = this.emailsText();
    const trimmed = raw.trim();

    // No error on initial empty/whitespace input
    if (!trimmed) {
      return [];
    }

    const parts = raw.split(',');

    let hasEmptyEntry = false;
    const validEmails: string[] = [];
    const invalid: string[] = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const part of parts) {
      const email = part.trim();
      if (!email) {
        hasEmptyEntry = true;
        continue;
      }
      if (!emailRegex.test(email)) {
        invalid.push(email);
      } else {
        validEmails.push(email);
      }
    }

    if (hasEmptyEntry || invalid.length > 0) {
      if (invalid.length > 0) {
        return [
          `The following address${invalid.length > 1 ? 'es are' : ' is'} invalid: ${invalid.join(
            ', ',
          )}`,
        ];
      }
      return ['There are empty or whitespace-only entries in the list.'];
    }

    return [];
  });

  onEmailsInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement | null;
    if (!target) return;
    this.emailsText.set(target.value);
  }

  // Sends a specimen invitation to the signed-in user. Deliberately independent
  // of the email textarea: the API always mails the address on the token, so
  // neither the entered addresses nor their validity affect this.
  onSendSampleClick(): void {
    if (this.isSendingSample()) {
      return;
    }

    this.isSendingSample.set(true);

    this.participantDomain
      .sendSampleInvitation()
      .pipe(finalize(() => this.isSendingSample.set(false)))
      .subscribe({
        next: () => {
          this.snackBar.open(
            'Sample invitation sent to the email address on your account.',
            'Close',
            { duration: 5000 },
          );
        },
        error: (err) => {
          const message =
            err?.status === 404
              ? 'No email address is associated with your account, so a sample could not be sent.'
              : 'Failed to send the sample invitation.';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  onAddClick(): void {
    if (!this.emailsText().trim() || this.errors().length > 0 || this.isSubmitting()) {
      return;
    }

    const project = this.projectDomain.currentProject();
    const projectId = project?.id;
    if (!projectId) {
      this.apiError.set('Missing project id.');
      return;
    }

    this.isSubmitting.set(true);
    this.apiError.set(null);

    const raw = this.emailsText();
    const parts = raw.split(',');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const candidateEmails: string[] = [];

    for (const part of parts) {
      const email = part.trim();
      if (email && emailRegex.test(email)) {
        candidateEmails.push(email);
      }
    }

    const distinctCandidates = Array.from(new Set(candidateEmails.map((e) => e.toLowerCase())));

    this.participantDomain
      .loadProjectParticipants(projectId)
      .pipe(
        map((participants) => {
          const existing = new Set(
            participants.map((p) => p.email?.toLowerCase()).filter((e): e is string => !!e),
          );
          return distinctCandidates.filter((e) => !existing.has(e));
        }),
        switchMap((newEmails) => {
          if (newEmails.length === 0) {
            return of(void 0);
          }
          return this.participantDomain.inviteParticipants(projectId, newEmails);
        }),
        switchMap(() =>
          this.assessmentDomain.refreshAssessments().pipe(
            catchError(() => of([])),
            map(() => void 0),
          ),
        ),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/projects', projectId, 'participants'], {
            state: { showLoading: true },
          });
        },
        error: () => {
          this.apiError.set('Failed to send invitations.');
        },
      });
  }
}
